import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { ExternalLink, Star, Edit2, Trash2, Lock, Users, Building2, Globe, Plus, Search, History, AlertCircle, Check, ArrowLeft, Loader2, Tag, Settings as SettingsIcon } from 'lucide-react';

const VISIBILITY = {
  private: { label: 'Private', icon: Lock,      color: 'bg-slate-100 text-slate-700 border-slate-300',         helper: 'Only you can see this' },
  shared:  { label: 'Shared',  icon: Users,     color: 'bg-blue-50 text-blue-700 border-blue-200',             helper: 'Selected people can view' },
  team:    { label: 'Team',    icon: Building2, color: 'bg-amber-50 text-amber-700 border-amber-200',          helper: 'A whole role can view' },
  public:  { label: 'Public',  icon: Globe,     color: 'bg-emerald-50 text-emerald-700 border-emerald-200',    helper: 'Everyone in the org can view' },
};

const ROLE_LABELS = { admin: 'Admin', manager: 'Manager', restricted_user: 'Restricted User' };

function initialsFor(name = '') {
  return name.split(' ').filter(Boolean).map(s => s[0]).slice(0, 2).join('').toUpperCase() || '?';
}

export default function ZohoReportsHub() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [reports, setReports] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [shareableUsers, setShareableUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [editorState, setEditorState] = useState({ open: false, mode: 'create', report: null });
  const [auditLogState, setAuditLogState] = useState({ open: false, report: null });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [categoryManagerOpen, setCategoryManagerOpen] = useState(false);

  const isAdmin = user?.role === 'admin';

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [r, su, cat] = await Promise.all([
        api.get('/zoho-reports'),
        api.get('/zoho-reports/shareable-users'),
        api.get('/zoho-reports/categories'),
      ]);
      setReports(r.data.reports || []);
      setShareableUsers(su.data.users || []);
      setCategories(cat.data.categories || []);
    } catch { toast.error('Failed to load reports'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const reloadCategories = async () => {
    try {
      const r = await api.get('/zoho-reports/categories');
      setCategories(r.data.categories || []);
    } catch { /* ignore */ }
  };

  const visible = useMemo(() => {
    let arr = reports;
    if (tab === 'favorites') arr = arr.filter(r => r.is_favorite);
    if (tab === 'mine') arr = arr.filter(r => r.created_by_id === user?.id);
    if (tab === 'shared') arr = arr.filter(r => r.created_by_id !== user?.id);
    if (categoryFilter) arr = arr.filter(r => (r.category || 'Uncategorized') === categoryFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter(r => r.name.toLowerCase().includes(q) || (r.description || '').toLowerCase().includes(q) || (r.category || '').toLowerCase().includes(q));
    }
    return arr;
  }, [reports, tab, search, categoryFilter, user]);

  const counts = useMemo(() => ({
    all: reports.length,
    favorites: reports.filter(r => r.is_favorite).length,
    mine: reports.filter(r => r.created_by_id === user?.id).length,
    shared: reports.filter(r => r.created_by_id !== user?.id).length,
  }), [reports, user]);

  const handleSave = async (payload) => {
    try {
      if (editorState.mode === 'create') {
        await api.post('/zoho-reports', payload);
        toast.success('Report created');
      } else {
        await api.put(`/zoho-reports/${editorState.report.id}`, payload);
        toast.success('Report updated');
      }
      setEditorState({ open: false, mode: 'create', report: null });
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/zoho-reports/${confirmDelete.id}`);
      toast.success('Report deleted');
      setConfirmDelete(null);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); setConfirmDelete(null); }
  };

  const handleOpen = async (report) => {
    try { await api.put(`/zoho-reports/${report.id}/opened`); } catch {}
    window.open(report.url, '_blank', 'noopener,noreferrer');
  };

  const handleToggleFavorite = async (report) => {
    try {
      await api.put(`/zoho-reports/${report.id}`, { is_favorite: !report.is_favorite });
      setReports(p => p.map(r => r.id === report.id ? { ...r, is_favorite: !r.is_favorite } : r));
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="min-h-full bg-slate-50 p-6">
      <div className="max-w-7xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <Button variant="ghost" size="sm" className="text-xs text-slate-500 hover:text-slate-900 -ml-2 mb-2" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-3.5 h-3.5 mr-1.5" />Back
            </Button>
            <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Zoho CRM &middot; Reports Hub</p>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Saved Reports</h1>
            {user && <p className="text-xs text-slate-500 mt-1">Logged in as <span className="font-semibold text-slate-700">{user.full_name}</span> &middot; {ROLE_LABELS[user.role] || user.role}</p>}
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button variant="outline" size="sm" className="text-xs h-9" onClick={() => setAuditLogState({ open: true, report: null })} data-testid="open-audit-log-btn">
                <History className="w-3.5 h-3.5 mr-1.5" />Audit Log
              </Button>
            )}
            <Button size="sm" className="text-xs h-9 bg-slate-900 hover:bg-slate-800" onClick={() => setEditorState({ open: true, mode: 'create', report: null })} data-testid="new-report-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" />New Report
            </Button>
          </div>
        </div>

        {/* Tabs + Search */}
        <Card className="border p-3 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1 bg-slate-100 p-0.5 rounded-md">
            {[
              { id: 'all',       label: 'All',            count: counts.all },
              { id: 'favorites', label: 'Favorites',      count: counts.favorites, icon: 'star' },
              { id: 'mine',      label: 'My Reports',     count: counts.mine },
              { id: 'shared',    label: 'Shared with me', count: counts.shared },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-3 py-1.5 rounded text-[11px] font-medium transition-colors flex items-center gap-1.5 ${tab === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid={`tab-${t.id}`}
              >
                {t.icon === 'star' && (
                  <Star className={`w-3 h-3 ${tab === t.id ? 'fill-amber-400 text-amber-400' : 'text-amber-400'}`} />
                )}
                {t.label}
                <span className="text-[9px] text-slate-400">{t.count}</span>
              </button>
            ))}
          </div>
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search reports..." className="pl-8 h-8 text-xs" data-testid="reports-search" />
          </div>
          <div className="flex items-center gap-2">
            <Select value={categoryFilter || 'all'} onValueChange={v => setCategoryFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-8 text-xs w-44" data-testid="category-filter">
                <span className="flex items-center gap-1.5"><Tag className="w-3 h-3 text-slate-400" /><SelectValue placeholder="All Categories" /></span>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(c => (
                  <SelectItem key={c.id} value={c.name}>
                    <span className="flex items-center justify-between gap-3 w-full">
                      <span>{c.name}</span>
                      <span className="text-[9px] text-slate-400">{c.report_count}</span>
                    </span>
                  </SelectItem>
                ))}
                <SelectItem value="Uncategorized">Uncategorized</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={() => setCategoryManagerOpen(true)} className="h-8 text-xs" data-testid="manage-categories-btn">
              <SettingsIcon className="w-3 h-3 mr-1" /> Manage
            </Button>
          </div>
        </Card>

        {/* Grid */}
        {loading ? (
          <div className="py-20 text-center"><Loader2 className="w-6 h-6 text-slate-300 animate-spin mx-auto" /></div>
        ) : visible.length === 0 ? (
          <Card className="border p-12 text-center">
            <p className="text-sm text-slate-400">{reports.length === 0 ? 'No saved reports yet.' : 'No reports match your filter.'}</p>
            {reports.length === 0 && (
              <Button size="sm" className="mt-4 text-xs bg-slate-900 hover:bg-slate-800" onClick={() => setEditorState({ open: true, mode: 'create', report: null })}>
                <Plus className="w-3 h-3 mr-1" />Create First Report
              </Button>
            )}
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {visible.map(r => (
              <ReportCard
                key={r.id}
                report={r}
                user={user}
                shareableUsers={shareableUsers}
                onEdit={() => setEditorState({ open: true, mode: 'edit', report: r })}
                onDelete={() => setConfirmDelete(r)}
                onAuditLog={isAdmin ? () => setAuditLogState({ open: true, report: r }) : null}
                onOpen={() => handleOpen(r)}
                onToggleFavorite={() => handleToggleFavorite(r)}
              />
            ))}
          </div>
        )}

        {/* Dialogs */}
        <ReportEditorDialog
          open={editorState.open}
          mode={editorState.mode}
          report={editorState.report}
          user={user}
          shareableUsers={shareableUsers}
          categories={categories}
          onCategoriesChanged={reloadCategories}
          onClose={() => setEditorState({ open: false, mode: 'create', report: null })}
          onSave={handleSave}
        />
        <CategoryManagerDialog
          open={categoryManagerOpen}
          categories={categories}
          onClose={() => setCategoryManagerOpen(false)}
          onChanged={reloadCategories}
        />
        <AuditLogDialog
          open={auditLogState.open}
          report={auditLogState.report}
          onClose={() => setAuditLogState({ open: false, report: null })}
        />
        <Dialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-sm flex items-center gap-2"><AlertCircle className="w-4 h-4 text-red-500" />Delete this report?</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-slate-600">"<span className="font-semibold text-slate-800">{confirmDelete?.name}</span>" will be permanently removed. This cannot be undone.</p>
            {confirmDelete && isAdmin && confirmDelete.created_by_id !== user?.id && (
              <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-800">
                You're deleting a report owned by <span className="font-semibold">{confirmDelete.added_by}</span>. This action will be recorded in the audit log.
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" className="text-xs h-8" onClick={() => setConfirmDelete(null)}>Cancel</Button>
              <Button size="sm" className="text-xs h-8 bg-red-600 hover:bg-red-700" onClick={doDelete} data-testid="confirm-delete-btn">Delete</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}


// ─── Card ────────────────────────────────────────────────────

function ReportCard({ report, user, shareableUsers, onEdit, onDelete, onAuditLog, onOpen, onToggleFavorite }) {
  const vis = VISIBILITY[report.visibility] || VISIBILITY.public;
  const VisIcon = vis.icon;
  const isOwner = report.created_by_id === user?.id;
  const editable = report._can_edit ?? (user?.role === 'admin' || isOwner);
  const ownerLabel = isOwner ? 'You' : (report.added_by || 'Unknown');
  const initials = initialsFor(report.added_by || '');

  return (
    <Card className="border p-0 overflow-hidden hover:shadow-md transition-all group" data-testid={`report-card-${report.id}`}>
      <div className="px-4 pt-4 pb-2 flex items-start justify-between gap-2">
        <button onClick={onToggleFavorite} className="text-amber-400 hover:text-amber-500 transition-colors flex-shrink-0" title={report.is_favorite ? 'Unfavorite' : 'Favorite'}>
          <Star className={`w-4 h-4 ${report.is_favorite ? 'fill-amber-400' : ''}`} />
        </button>
        <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${vis.color}`} title={vis.helper}>
          <VisIcon className="w-2.5 h-2.5 mr-1" />{vis.label}
        </Badge>
      </div>
      <div className="px-4 pb-3">
        <h3 className="text-sm font-bold text-slate-900 leading-snug">{report.name}</h3>
        {report.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{report.description}</p>}
        <div className="flex items-center gap-2 mt-3 text-[11px] text-slate-500 flex-wrap">
          <div className="flex items-center gap-1.5">
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold ${isOwner ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-600'}`}>{initials}</span>
            <span className={isOwner ? 'font-semibold text-violet-700' : 'text-slate-600'}>{ownerLabel}</span>
          </div>
          <span className="text-slate-300">&middot;</span>
          <span>{report.category}</span>
          <span className="text-slate-300">&middot;</span>
          <span>{(report.created_at || '').slice(0, 10)}</span>
        </div>

        {report.visibility === 'shared' && (report.shared_with_users || []).length > 0 && (
          <div className="flex items-center gap-1 mt-2 flex-wrap">
            <span className="text-[10px] text-slate-400">Shared with:</span>
            {report.shared_with_users.map(uid => {
              const u = shareableUsers.find(x => x.id === uid);
              return u ? <Badge key={uid} variant="outline" className="text-[9px] bg-blue-50 text-blue-700 border-blue-200">{u.full_name}</Badge> : null;
            })}
          </div>
        )}
        {report.visibility === 'team' && (report.shared_with_roles || []).length > 0 && (
          <div className="flex items-center gap-1 mt-2 flex-wrap">
            <span className="text-[10px] text-slate-400">Visible to roles:</span>
            {report.shared_with_roles.map(role => (
              <Badge key={role} variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200">{ROLE_LABELS[role] || role}</Badge>
            ))}
          </div>
        )}
      </div>

      <div className="px-4 py-3 border-t bg-slate-50/50 flex items-center justify-between">
        <Button size="sm" variant="outline" className="h-7 text-xs gap-1.5" onClick={onOpen} data-testid={`open-report-${report.id}`}>
          <ExternalLink className="w-3 h-3" />Open in Zoho
        </Button>
        <TooltipProvider>
          <div className="flex items-center gap-0.5">
            {onAuditLog && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={onAuditLog}>
                    <History className="w-3.5 h-3.5 text-slate-400 hover:text-amber-500" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-[10px]">View audit log</TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={editable ? -1 : 0}>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={!editable} onClick={editable ? onEdit : undefined} data-testid={`edit-report-${report.id}`}>
                    <Edit2 className={`w-3.5 h-3.5 ${editable ? 'text-slate-500 hover:text-violet-600' : 'text-slate-300'}`} />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-[10px]">
                {editable
                  ? (user?.role === 'admin' && !isOwner ? `Admin edit — owned by ${report.added_by}` : 'Edit report')
                  : `Only ${report.added_by} can edit this`}
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={editable ? -1 : 0}>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={!editable} onClick={editable ? onDelete : undefined} data-testid={`delete-report-${report.id}`}>
                    <Trash2 className={`w-3.5 h-3.5 ${editable ? 'text-slate-500 hover:text-red-500' : 'text-slate-300'}`} />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-[10px]">
                {editable
                  ? (user?.role === 'admin' && !isOwner ? `Admin delete — owned by ${report.added_by}` : 'Delete report')
                  : `Only ${report.added_by} can delete this`}
              </TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      </div>
    </Card>
  );
}


// ─── Editor Dialog ───────────────────────────────────────────

function ReportEditorDialog({ open, mode, report, user, shareableUsers, categories = [], onCategoriesChanged, onClose, onSave }) {
  const [form, setForm] = useState({
    name: '', url: '', category: '', description: '', is_favorite: false,
    visibility: '', shared_with_users: [], shared_with_roles: [],
  });
  const [userSearch, setUserSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [openKey, setOpenKey] = useState('');

  // Initialize on open
  useEffect(() => {
    if (!open) return;
    const key = `${mode}-${report?.id || 'new'}-${Date.now()}`;
    if (key === openKey) return;
    setOpenKey(key);
    if (mode === 'edit' && report) {
      setForm({
        name: report.name, url: report.url, category: report.category || '', description: report.description || '',
        is_favorite: !!report.is_favorite, visibility: report.visibility || 'private',
        shared_with_users: report.shared_with_users || [],
        shared_with_roles: report.shared_with_roles || [],
      });
    } else {
      setForm({ name: '', url: '', category: '', description: '', is_favorite: false, visibility: '', shared_with_users: [], shared_with_roles: [] });
    }
    setUserSearch('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, report?.id]);

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    if (!form.url.trim()) { toast.error('URL required'); return; }
    if (!/^https?:\/\//.test(form.url.trim())) { toast.error('URL must start with http:// or https://'); return; }
    if (!form.visibility) { toast.error('Pick a visibility level'); return; }
    if (form.visibility === 'shared' && form.shared_with_users.length === 0) {
      toast.error('Pick at least one user to share with'); return;
    }
    if (form.visibility === 'team' && form.shared_with_roles.length === 0) {
      toast.error('Pick at least one role'); return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (payload.visibility === 'private' || payload.visibility === 'public') {
        payload.shared_with_users = []; payload.shared_with_roles = [];
      } else if (payload.visibility === 'shared') {
        payload.shared_with_roles = [];
      } else if (payload.visibility === 'team') {
        payload.shared_with_users = [];
      }
      await onSave(payload);
    } finally { setSubmitting(false); }
  };

  const isAdminEditingOthers = mode === 'edit' && report && user?.role === 'admin' && report.created_by_id !== user?.id;
  const filteredUsers = shareableUsers.filter(u => !userSearch || u.full_name.toLowerCase().includes(userSearch.toLowerCase()));

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base">{mode === 'edit' ? 'Edit Report' : 'New Report'}</DialogTitle>
        </DialogHeader>

        {isAdminEditingOthers && (
          <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-800 flex items-start gap-2">
            <AlertCircle className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold">Admin override</p>
              <p>You're editing a report owned by <span className="font-semibold">{report.added_by}</span>. Changes will be recorded in the audit log.</p>
            </div>
          </div>
        )}

        <div className="space-y-3 mt-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium mb-1 block">Name *</Label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Monthly Sales Pipeline" className="h-9 text-sm" data-testid="report-name-input" />
            </div>
            <div>
              <Label className="text-xs font-medium mb-1 block">Category</Label>
              <CategorySelect
                value={form.category}
                onChange={(v) => setForm(p => ({ ...p, category: v }))}
                categories={categories}
                onCategoryCreated={onCategoriesChanged}
              />
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium mb-1 block">Zoho Report URL *</Label>
            <Input value={form.url} onChange={e => setForm(p => ({ ...p, url: e.target.value }))} placeholder="https://crm.zoho.com/..." className="h-9 text-xs font-mono" data-testid="report-url-input" />
          </div>
          <div>
            <Label className="text-xs font-medium mb-1 block">Description</Label>
            <Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Brief context — what does this report show?" className="text-xs min-h-[50px]" />
          </div>

          <div className="rounded-md border bg-slate-50/40 p-3">
            <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5 mb-2.5">
              <Lock className="w-3 h-3" />Who can see this report? *
            </Label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(VISIBILITY).map(([key, v]) => {
                const Icon = v.icon;
                const active = form.visibility === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setForm(p => ({ ...p, visibility: key }))}
                    className={`flex items-start gap-2 p-2.5 rounded-md border-2 text-left transition-all ${active ? 'border-violet-500 bg-violet-50/50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                    data-testid={`visibility-${key}`}
                  >
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${active ? 'bg-violet-500' : 'bg-slate-100'}`}>
                      {active ? <Check className="w-3 h-3 text-white" /> : <Icon className="w-3 h-3 text-slate-400" />}
                    </div>
                    <div className="flex-1">
                      <p className={`text-xs font-semibold ${active ? 'text-violet-700' : 'text-slate-700'}`}>{v.label}</p>
                      <p className="text-[10px] text-slate-500">{v.helper}</p>
                    </div>
                  </button>
                );
              })}
            </div>

            {form.visibility === 'shared' && (
              <div className="mt-3 pt-3 border-t border-slate-200">
                <Label className="text-[11px] font-medium text-slate-600 mb-1.5 block">Pick people to share with</Label>
                <Input value={userSearch} onChange={e => setUserSearch(e.target.value)} placeholder="Search by name..." className="h-8 text-xs mb-2" />
                <div className="border rounded-md bg-white max-h-44 overflow-y-auto">
                  {filteredUsers.length === 0 ? (
                    <p className="text-[11px] text-slate-400 py-3 text-center">{shareableUsers.length === 0 ? 'No other admin/manager users available.' : 'No matching users.'}</p>
                  ) : filteredUsers.map(u => {
                    const checked = form.shared_with_users.includes(u.id);
                    return (
                      <label key={u.id} className={`flex items-center gap-2 px-2.5 py-1.5 cursor-pointer hover:bg-slate-50 ${checked ? 'bg-blue-50/50' : ''}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => setForm(p => ({ ...p, shared_with_users: checked ? p.shared_with_users.filter(id => id !== u.id) : [...p.shared_with_users, u.id] }))}
                          className="w-3.5 h-3.5 rounded border-slate-300 text-violet-600"
                        />
                        <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-600 text-[9px] font-bold flex items-center justify-center">{initialsFor(u.full_name)}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-slate-800 leading-tight">{u.full_name}</p>
                          <p className="text-[10px] text-slate-400">{ROLE_LABELS[u.role] || u.role} &middot; {u.email}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
                <p className="text-[10px] text-slate-400 mt-1">{form.shared_with_users.length} selected. Trainers and restricted users are hidden.</p>
              </div>
            )}

            {form.visibility === 'team' && (
              <div className="mt-3 pt-3 border-t border-slate-200">
                <Label className="text-[11px] font-medium text-slate-600 mb-1.5 block">Pick role(s)</Label>
                <div className="space-y-1">
                  {Object.entries(ROLE_LABELS).map(([role, label]) => {
                    const checked = form.shared_with_roles.includes(role);
                    return (
                      <label key={role} className={`flex items-center gap-2 px-2.5 py-1.5 rounded cursor-pointer ${checked ? 'bg-amber-50' : 'hover:bg-slate-50'}`} data-testid={`team-role-${role}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => setForm(p => ({ ...p, shared_with_roles: checked ? p.shared_with_roles.filter(r => r !== role) : [...p.shared_with_roles, role] }))}
                          className="w-3.5 h-3.5 rounded border-slate-300 text-amber-600"
                        />
                        <span className="text-xs text-slate-700 flex-1">{label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {form.visibility === 'public' && (
              <div className="mt-3 pt-3 border-t border-slate-200">
                <p className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2.5 py-1.5">All admin / manager / restricted users in this org will see this report.</p>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t mt-3">
          <Button variant="outline" size="sm" className="text-xs h-9" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="text-xs h-9 bg-slate-900 hover:bg-slate-800" onClick={submit} disabled={submitting} data-testid="save-report-btn">
            {submitting ? 'Saving...' : (mode === 'edit' ? 'Save Changes' : 'Create Report')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ─── Audit Log Dialog ────────────────────────────────────────

function AuditLogDialog({ open, report, onClose }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const url = report ? `/zoho-reports/audit-log?report_id=${report.id}` : '/zoho-reports/audit-log';
    api.get(url).then(r => setEntries(r.data.entries || [])).catch(() => {}).finally(() => setLoading(false));
  }, [open, report?.id]);

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <History className="w-4 h-4 text-amber-500" />
            Audit Log
            {report && <span className="text-xs text-slate-400 font-normal">&middot; {report.name}</span>}
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500">Tracks every admin override on reports owned by other users. Read-only.</p>
        {loading ? (
          <div className="py-8 text-center"><Loader2 className="w-5 h-5 text-slate-300 animate-spin mx-auto" /></div>
        ) : entries.length === 0 ? (
          <p className="text-xs text-slate-400 py-12 text-center">No admin actions recorded yet.</p>
        ) : (
          <div className="space-y-2 mt-2">
            {entries.map(e => {
              const action = e.action === 'edited' ? { color: 'bg-blue-50 text-blue-700 border-blue-200', label: 'Edited' }
                          : e.action === 'visibility_changed' ? { color: 'bg-violet-50 text-violet-700 border-violet-200', label: 'Visibility changed' }
                          : { color: 'bg-red-50 text-red-700 border-red-200', label: 'Deleted' };
              return (
                <div key={e.id} className="border rounded-md p-3 bg-slate-50/40">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`text-[9px] uppercase tracking-wider ${action.color}`}>{action.label}</Badge>
                      <p className="text-xs font-semibold text-slate-800">{e.report_name}</p>
                    </div>
                    <span className="text-[10px] text-slate-400">{(e.timestamp || '').slice(0, 19).replace('T', ' ')}</span>
                  </div>
                  <p className="text-[11px] text-slate-600">
                    <span className="font-semibold">{e.actor_name}</span> ({ROLE_LABELS[e.actor_role] || e.actor_role}) on a report owned by <span className="font-semibold">{e.original_owner_name}</span>.
                  </p>
                  {e.notes && <p className="text-[10px] text-slate-500 mt-1 italic">{e.notes}</p>}
                </div>
              );
            })}
          </div>
        )}
        <div className="flex justify-end pt-2 border-t mt-2">
          <Button variant="outline" size="sm" className="text-xs h-8" onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* =============================================================================
 * CATEGORY SELECT (with inline "+ Create new")
 * =============================================================================*/
function CategorySelect({ value, onChange, categories, onCategoryCreated }) {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);

  const submitNew = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      const r = await api.post('/zoho-reports/categories', { name });
      const created = r.data.category?.name;
      if (created) onChange(created);
      setNewName('');
      setCreating(false);
      onCategoryCreated && onCategoryCreated();
      toast.success(`Category "${created}" created`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create category');
    } finally { setBusy(false); }
  };

  if (creating) {
    return (
      <div className="flex gap-1.5">
        <Input autoFocus value={newName} onChange={e => setNewName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') submitNew(); if (e.key === 'Escape') { setCreating(false); setNewName(''); } }}
          placeholder="New category name" className="h-9 text-sm" data-testid="cat-new-input" />
        <Button size="sm" onClick={submitNew} disabled={busy || !newName.trim()} className="h-9 text-xs">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Add'}
        </Button>
        <Button size="sm" variant="outline" onClick={() => { setCreating(false); setNewName(''); }} className="h-9 text-xs">Cancel</Button>
      </div>
    );
  }

  return (
    <Select value={value || '__none__'} onValueChange={(v) => {
      if (v === '__create__') { setCreating(true); return; }
      onChange(v === '__none__' ? '' : v);
    }}>
      <SelectTrigger className="h-9 text-sm" data-testid="cat-select-trigger">
        <span className="flex items-center gap-1.5"><Tag className="w-3 h-3 text-slate-400" /><SelectValue placeholder="Pick a category" /></span>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__"><span className="text-slate-500 italic">Uncategorized</span></SelectItem>
        {categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
        <SelectItem value="__create__" data-testid="cat-create-option"><span className="text-blue-600 flex items-center gap-1.5"><Plus className="w-3 h-3" /> Create new category</span></SelectItem>
      </SelectContent>
    </Select>
  );
}

/* =============================================================================
 * CATEGORY MANAGER DIALOG
 * =============================================================================*/
function CategoryManagerDialog({ open, categories, onClose, onChanged }) {
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) setNewName(''); }, [open]);

  const add = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      await api.post('/zoho-reports/categories', { name: newName.trim() });
      setNewName('');
      onChanged && onChanged();
      toast.success('Category added');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add');
    } finally { setBusy(false); }
  };

  const remove = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"?`)) return;
    try {
      await api.delete(`/zoho-reports/categories/${cat.id}`);
      onChanged && onChanged();
      toast.success('Deleted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cannot delete (in use?)');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md p-0" data-testid="cat-manager-dialog">
        <DialogHeader className="px-5 py-3.5 border-b bg-slate-50">
          <DialogTitle className="text-sm font-semibold flex items-center gap-1.5"><Tag className="w-4 h-4" /> Manage Categories</DialogTitle>
        </DialogHeader>
        <div className="p-5 space-y-3">
          <div className="flex gap-1.5">
            <Input value={newName} onChange={e => setNewName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') add(); }}
              placeholder="New category name" className="h-9 text-sm" data-testid="cat-mgr-input" />
            <Button onClick={add} disabled={busy || !newName.trim()} className="h-9 text-xs bg-slate-900 hover:bg-slate-800" data-testid="cat-mgr-add">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Plus className="w-3 h-3 mr-1" /> Add</>}
            </Button>
          </div>
          <div className="border rounded-md divide-y">
            {categories.length === 0 && <div className="text-xs text-slate-400 italic text-center py-6">No categories yet</div>}
            {categories.map(c => (
              <div key={c.id} className="flex items-center justify-between px-3 py-2 hover:bg-slate-50">
                <div className="flex items-center gap-2 min-w-0">
                  <Tag className="w-3 h-3 text-slate-400 flex-shrink-0" />
                  <span className="text-sm text-slate-800 truncate">{c.name}</span>
                  {c.system && <span className="text-[9px] uppercase tracking-wider text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">System</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-slate-400">{c.report_count} report{c.report_count !== 1 ? 's' : ''}</span>
                  {!c.system && c.report_count === 0 && (
                    <button onClick={() => remove(c)} className="p-1 hover:bg-rose-50 rounded text-rose-500" data-testid={`cat-delete-${c.id}`} title="Delete">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
